<template>
    <div
        class="bg-bg relative flex flex-col"
        :style="{ paddingBottom: 'calc(1rem + var(--safezone-bottom, 0px))' }"
    >
        <!-- While input is locked (e.g. the bot is throwing), dim the grid with an
             overlay that sits below the Re-Do button (z-30) so the player can still
             tap Re-Do to edit their last turn. Editing clears `disabled` and the overlay. -->
        <div v-if="disabledAndNotEditing" class="absolute inset-0 z-20 bg-bg/60"></div>

        <CheckoutAttemptsModal
            :is-visible="showCheckoutAttemptsModal"
            :available-attempts="availableDoubleAttempts"
            :is-submitting="isSubmitting"
            @select="handleCheckoutAttemptsSelect"
            @cancel="handleCheckoutAttemptsCancel"
        />

        <div class="flex flex-col">
            <div class="relative flex items-center gap-4 p-2">
                <GridToggleButton
                    mode="dart"
                    :disabled="disableGridToggle"
                    @toggle="$emit('toggle-grid')"
                />

                <div class="relative flex-1">
                    <div class="flex justify-evenly gap-4">
                        <div
                            class="bg-card border-stroke-1 flex h-9 flex-1 items-center justify-center border-1 transition-all"
                            :class="{
                                'bg-card-2 border-red-500': currentDarts.length === 0,
                                'border-green-500': currentDarts.length === 0,
                            }"
                        >
                            <span class="font-title font-semibold text-white">{{
                                formatDartDisplay(currentDarts[0]) || ''
                            }}</span>
                        </div>
                        <div
                            class="bg-card border-stroke-1 flex h-9 flex-1 items-center justify-center border-1 transition-all"
                            :class="{
                                'border-green-500': currentDarts.length === 1,
                            }"
                        >
                            <span class="font-title font-semibold text-white">{{
                                formatDartDisplay(currentDarts[1]) || ''
                            }}</span>
                        </div>
                        <div
                            class="bg-card border-stroke-1 flex h-9 flex-1 items-center justify-center border-1 transition-all"
                            :class="{
                                'border-green-500': currentDarts.length === 2,
                            }"
                        >
                            <span class="font-title font-semibold text-white">{{
                                formatDartDisplay(currentDarts[2]) || ''
                            }}</span>
                        </div>
                    </div>
                </div>

                <button
                    @click="submitTurn"
                    :disabled="disabledAndNotEditing || currentDarts.length === 0 || isSubmitting"
                    data-testid="dart-confirm"
                    class="font-title clip-left-corner-small relative h-9 bg-red-600 px-4 text-xs font-semibold tracking-tight text-white uppercase transition-colors hover:bg-red-700"
                >
                    <span
                        v-if="isSubmitting"
                        class="absolute inset-0 flex items-center justify-center bg-red-600"
                    >
                        <svg
                            class="h-5 w-5 animate-spin text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                class="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                            ></circle>
                            <path
                                class="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                    </span>
                    <span :class="{ 'opacity-0': isSubmitting }">{{ $t('general.confirm') }}</span>
                </button>
            </div>

            <div class="border-stroke-1 divide-stroke-1 flex divide-x border-t">
                <button
                    :disabled="disabledAndNotEditing || needsDoubleToStart || needsDoubleOrTripleToStart || isSubmitting"
                    @click="setMultiplier('S')"
                    data-testid="dart-mult-single"
                    :class="[
                        'flex-1 py-2 text-lg font-semibold uppercase transition-colors',
                        selectedMultiplier === 'S' &&
                        !needsDoubleToStart &&
                        !needsDoubleOrTripleToStart
                            ? 'bg-red-600 text-white'
                            : 'bg-bg hover:bg-card text-white',
                        needsDoubleToStart || needsDoubleOrTripleToStart
                            ? 'cursor-not-allowed opacity-30'
                            : '',
                    ]"
                >
                    {{ $t('score_grid.single') }}
                </button>
                <button
                    :disabled="disabledAndNotEditing || isSubmitting"
                    @click="setMultiplier('D')"
                    :class="[
                        'flex-1 py-2 text-lg font-semibold uppercase transition-colors',
                        selectedMultiplier === 'D'
                            ? 'bg-red-600 text-white'
                            : 'bg-bg hover:bg-card text-white',
                    ]"
                >
                    {{ $t('score_grid.double') }}
                </button>
                <button
                    :disabled="disabledAndNotEditing || isSubmitting || (needsDoubleToStart && !needsDoubleOrTripleToStart)"
                    @click="setMultiplier('T')"
                    :class="[
                        'flex-1 py-2 text-lg font-semibold uppercase transition-colors',
                        selectedMultiplier === 'T' &&
                        !(needsDoubleToStart && !needsDoubleOrTripleToStart)
                            ? 'bg-red-600 text-white'
                            : 'bg-bg hover:bg-card text-white',
                        needsDoubleToStart && !needsDoubleOrTripleToStart
                            ? 'cursor-not-allowed opacity-30'
                            : '',
                    ]"
                >
                    {{ $t('score_grid.triple') }}
                </button>
            </div>

            <div class="border-stroke-1 divide-stroke-1 grid grid-cols-5 divide-x border-t">
                <button
                    v-for="num in [
                        1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
                    ]"
                    :key="num"
                    :disabled="
                        disabledAndNotEditing || isSubmitting ||
                        ((needsDoubleToStart || needsDoubleOrTripleToStart) &&
                            selectedMultiplier === 'S') ||
                        ((needsDoubleToStart && !needsDoubleOrTripleToStart) &&
                            selectedMultiplier === 'T')
                    "
                    @click="selectNumber(num)"
                    data-testid="dart-number"
                    :data-num="num"
                    class="border-stroke-1 bg-bg hover:bg-card flex h-13 flex-col items-center justify-center border-b font-bold text-white transition-colors"
                    :class="{
                        'cursor-not-allowed opacity-30':
                            ((needsDoubleToStart || needsDoubleOrTripleToStart) &&
                                selectedMultiplier === 'S') ||
                            ((needsDoubleToStart && !needsDoubleOrTripleToStart) &&
                                selectedMultiplier === 'T'),
                    }"
                >
                    <div class="text-2xl">{{ num }}</div>
                    <div v-if="selectedMultiplier !== 'S'" class="text-titles text-xs">
                        ({{ getScoreForNumber(num) }})
                    </div>
                </button>
            </div>

            <div class="border-stroke-1 divide-stroke-1 grid grid-cols-5 divide-x border-b">
                <button
                    :disabled="isSubmitting || (!matchStore.editingTurn && !hasLastTurn)"
                    @click="matchStore.editingTurn ? cancelEditing() : loadLastTurn()"
                    class="relative z-30 bg-bg hover:bg-card min-w-0 px-1 py-2.5 text-lg font-bold text-white uppercase transition-colors disabled:cursor-not-allowed"
                    :class="disabledAndNotEditing ? 'disabled:opacity-20' : 'disabled:opacity-50'"
                >
                    <span class="block truncate">{{ matchStore.editingTurn ? $t('general.cancel') : $t('score_grid.redo') }}</span>
                </button>
                <button
                    :disabled="disabledAndNotEditing || isSubmitting"
                    @click="addBull"
                    class="bg-bg hover:bg-card py-2.5 text-lg font-bold text-white uppercase transition-colors"
                >
                    50
                </button>
                <button
                    :disabled="disabledAndNotEditing || needsDoubleToStart || needsDoubleOrTripleToStart || isSubmitting"
                    @click="addOuterBull"
                    class="bg-bg hover:bg-card py-2.5 text-lg font-bold text-white uppercase transition-colors"
                    :class="{
                        'cursor-not-allowed opacity-30':
                            disabledAndNotEditing || needsDoubleToStart || needsDoubleOrTripleToStart,
                    }"
                >
                    25
                </button>
                <button
                    :disabled="disabledAndNotEditing || isSubmitting"
                    @click="addMiss"
                    class="bg-bg hover:bg-card py-2.5 text-lg font-bold text-white uppercase transition-colors"
                >
                    {{ $t('score_grid.miss') }}
                </button>
                <button
                    :disabled="disabledAndNotEditing || isSubmitting || currentDarts.length === 0"
                    @click="removeLastDart"
                    class="bg-bg hover:bg-card min-w-0 px-1 py-2.5 text-lg font-bold text-white uppercase transition-colors"
                    :class="{
                        'disabled:cursor-not-allowed disabled:opacity-50': disabledAndNotEditing || currentDarts.length === 0,
                    }"
                >
                    <span class="block truncate">{{ $t('score_grid.undo') }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { computed } from 'vue'
import { DART_TYPES } from '@/constants/dartTypes.js'
import GridToggleButton from '@/components/ui/GridToggleButton.vue'
import CheckoutAttemptsModal from '@/components/CheckoutAttemptsModal.vue'
import { useDartScoring } from '@/composables/useDartScoring.js'
import doubleAttemptsCalculator from '@/services/doubleAttempts.js'

export default {
    name: 'DartScoreGrid',
    components: { GridToggleButton, CheckoutAttemptsModal },
    props: {
        currentPlayer: {
            type: Object,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        disableGridToggle: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['toggle-grid'],
    data() {
        return {
            currentDarts: [],
            selectedMultiplier: 'S',
            isSubmitting: false,
            turnPlayerId: null,
            showCheckoutAttemptsModal: false,
        }
    },
    watch: {
        'matchStore.editingTurn'(newEditingTurn) {
            if (newEditingTurn && newEditingTurn.darts && newEditingTurn.darts.length > 0) {
                this.clearCurrentTurn()
                this.turnPlayerId = this.currentPlayer.id
                this.currentDarts = newEditingTurn.darts.map((dart) => ({
                    dart_type: dart.dart_type,
                    dart_number: dart.dart_number,
                }))
            }
        },
        firstAvailableMultiplier: {
            immediate: true,
            handler(newVal) {
                this.selectedMultiplier = newVal
            },
        },
    },
    setup(props) {
        const {
            matchStore,
            settings,
            checkInType,
            requiresDoubleIn,
            requiresDoubleOrTripleIn,
            requiresSpecialOut,
            isFirstTurnInLeg,
            maxScore,
            remainingScore,
            effectiveCheckOutType,
            effectiveRemainingBefore,
            hasLastTurn,
            loadLastTurn,
            isDartValidForCheckout,
            submitDartData,
        } = useDartScoring(computed(() => props.currentPlayer))

        return {
            matchStore,
            settings,
            checkInType,
            requiresDoubleIn,
            requiresDoubleOrTripleIn,
            requiresSpecialOut,
            isFirstTurnInLeg,
            maxScore,
            remainingScore,
            effectiveCheckOutType,
            effectiveRemainingBefore,
            hasLastTurn,
            loadTurnForEditing: loadLastTurn,
            isDartValidForCheckout,
            submitDartData,
        }
    },
    computed: {
        isFirstDartOfTurn() {
            return this.currentDarts.length === 0
        },
        disabledAndNotEditing() {
            return this.disabled && !this.matchStore.editingTurn
        },
        firstAvailableMultiplier() {
            if (this.needsDoubleToStart || this.needsDoubleOrTripleToStart) return 'D'
            return 'S'
        },
        hasValidCheckInDart() {
            if (this.requiresDoubleOrTripleIn) {
                return this.currentDarts.some(
                    (d) =>
                        d.dart_type === 'D' ||
                        d.dart_type === 'T' ||
                        d.dart_type === DART_TYPES.BULL,
                )
            }
            if (this.requiresDoubleIn) {
                return this.currentDarts.some(
                    (d) => d.dart_type === 'D' || d.dart_type === DART_TYPES.BULL,
                )
            }
            return true
        },
        needsDoubleToStart() {
            return this.isFirstTurnInLeg && this.requiresDoubleIn && !this.hasValidCheckInDart
        },
        needsDoubleOrTripleToStart() {
            return (
                this.isFirstTurnInLeg && this.requiresDoubleOrTripleIn && !this.hasValidCheckInDart
            )
        },
        getScoreForNumber() {
            return (num) => {
                if (this.selectedMultiplier === 'D') return num * 2
                if (this.selectedMultiplier === 'T') return num * 3
                return num
            }
        },
        dartLimitReached() {
            return this.currentDarts.length >= 3
        },
        currentTurnScore() {
            return this.currentDarts.reduce((sum, dart) => {
                return sum + this.matchStore.calculateDartScore(dart)
            }, 0)
        },
        wouldFinishGame() {
            return (dart) => {
                if (!this.remainingScore) return false
                const dartScore = this.matchStore.calculateDartScore(dart)
                const totalScore = this.currentTurnScore + dartScore
                return this.remainingScore - totalScore === 0
            }
        },
        effectiveRemainingAfter() {
            if (this.effectiveRemainingBefore === null) return null
            return this.effectiveRemainingBefore - this.currentTurnScore
        },
        dartsForAttempts() {
            // The exact darts are known, so the double-attempt calculator only
            // needs each dart's score and whether it was a finishing-type dart.
            return this.currentDarts.map((dart) => ({
                value: this.matchStore.calculateDartScore(dart),
                isDouble: dart.dart_type === 'D' || dart.dart_type === DART_TYPES.BULL,
                isTriple: dart.dart_type === 'T',
            }))
        },
        availableDoubleAttempts() {
            if (this.effectiveRemainingBefore === null || this.currentDarts.length === 0) {
                return [0, 1, 2, 3]
            }

            return doubleAttemptsCalculator.computePossibleAttemptsFromDarts(
                this.effectiveRemainingBefore,
                this.dartsForAttempts,
                this.effectiveCheckOutType,
            )
        },
        isFinishAttempt() {
            if (this.currentDarts.length === 0) return false
            const isX01Game = this.matchStore.match.matchtype_id === 1
            if (!isX01Game) return false
            if (this.effectiveRemainingBefore === null) return false

            // A finish attempt is any turn in which at least one dart was thrown
            // from a one-dart-finish position (so it could have been at a double).
            return this.availableDoubleAttempts.some((count) => count >= 1)
        },
    },
    methods: {
        setMultiplier(multiplier) {
            if (this.disabledAndNotEditing || this.isSubmitting) return
            this.selectedMultiplier = multiplier
        },
        selectNumber(number) {
            if (this.disabledAndNotEditing || this.isSubmitting) return
            if (this.dartLimitReached) return

            if (
                (this.needsDoubleToStart || this.needsDoubleOrTripleToStart) &&
                this.selectedMultiplier === 'S'
            ) {
                return
            }

            const dart = {
                dart_type: this.selectedMultiplier,
                dart_number: number,
            }

            if (this.requiresSpecialOut && this.wouldFinishGame(dart)) {
                if (!this.isDartValidForCheckout(this.selectedMultiplier)) {
                    console.log('Cannot finish on this dart type')
                    return
                }
            }

            this.addDart(dart)
        },
        addBull() {
            if (this.disabledAndNotEditing || this.isSubmitting) return
            if (this.currentDarts.length < 3) {
                const dart = {
                    dart_type: DART_TYPES.BULL,
                    dart_number: 50,
                }

                if (this.requiresSpecialOut && this.wouldFinishGame(dart)) {
                    if (!this.isDartValidForCheckout(DART_TYPES.BULL)) {
                        console.log('Cannot finish on this dart type')
                        return
                    }
                }

                this.addDart(dart)
            }
        },
        addOuterBull() {
            if (this.disabledAndNotEditing || this.isSubmitting) return
            if (this.currentDarts.length < 3) {
                if (this.needsDoubleToStart || this.needsDoubleOrTripleToStart) {
                    return
                }

                const dart = {
                    dart_type: DART_TYPES.OUTER_BULL,
                    dart_number: 25,
                }

                if (this.requiresSpecialOut && this.wouldFinishGame(dart)) {
                    if (!this.isDartValidForCheckout(DART_TYPES.OUTER_BULL)) {
                        console.log('Cannot finish on outer bull - not a valid finishing dart')
                        return
                    }
                }

                this.addDart(dart)
            }
        },
        addMiss() {
            if (this.disabledAndNotEditing || this.isSubmitting) return
            if (this.currentDarts.length < 3) {
                this.addDart({
                    dart_type: DART_TYPES.MISS,
                    dart_number: 0,
                })
            }
        },
        addDart(dartData) {
            if (this.currentDarts.length === 0) {
                this.turnPlayerId = this.currentPlayer.id
            }
            this.currentDarts.push(dartData)
        },
        removeLastDart() {
            if (this.disabledAndNotEditing || this.isSubmitting) return
            if (this.currentDarts.length > 0) {
                this.currentDarts.pop()
            }
        },
        formatDartDisplay(dart) {
            if (!dart) return ''

            if (dart.dart_type === DART_TYPES.BULL) return '50'
            if (dart.dart_type === DART_TYPES.OUTER_BULL) return '25'
            if (dart.dart_type === DART_TYPES.MISS) return '-'

            return dart.dart_type === 'S'
                ? dart.dart_number.toString()
                : `${dart.dart_type}${dart.dart_number}`
        },
        clearCurrentTurn() {
            this.currentDarts = []
            this.selectedMultiplier = 'S'
            this.turnPlayerId = null
        },
        getCurrentDarts() {
            return this.currentDarts
        },
        resetDarts() {
            this.currentDarts = []
            this.selectedMultiplier = 'S'
            this.turnPlayerId = null
        },
        loadLastTurn() {
            this.loadTurnForEditing('score_grid.editing_hint_dart')
        },
        cancelEditing() {
            this.matchStore.clearEditingTurn()
            this.clearCurrentTurn()
        },
        async submitTurn() {
            if (this.disabledAndNotEditing) return
            if (this.currentDarts.length === 0) {
                return
            }

            // We know the exact darts, so the only open question is how many were
            // actually aimed at the checkout double. Ask on any finish attempt
            // (including a checkout), pre-filled with the plausible range.
            if (this.isFinishAttempt) {
                this.showCheckoutAttemptsModal = true
                return
            }

            await this.submitDarts()
        },
        async submitDarts(doubleAttempts = null) {
            this.isSubmitting = true
            this.showCheckoutAttemptsModal = false

            try {
                const profileId = this.matchStore.editingTurn
                    ? this.matchStore.editingTurn.profile_id
                    : this.turnPlayerId || this.currentPlayer.id

                let runningScore = 0
                let shouldStopAfterDart = false
                let dartsToSubmit = []

                for (let i = 0; i < this.currentDarts.length; i++) {
                    const dart = this.currentDarts[i]
                    const dartScore = this.matchStore.calculateDartScore(dart)
                    runningScore += dartScore

                    const newRemaining = this.remainingScore - runningScore
                    const isCheckout = newRemaining === 0
                    const isBust = newRemaining < 0 || newRemaining === 1

                    dartsToSubmit.push({ dart, dartIndex: i })

                    if (isCheckout || isBust) {
                        shouldStopAfterDart = true
                        break
                    }
                }

                if (!shouldStopAfterDart && dartsToSubmit.length < 3) {
                    while (dartsToSubmit.length < 3) {
                        dartsToSubmit.push({
                            dart: {
                                dart_type: DART_TYPES.MISS,
                                dart_number: 0,
                            },
                            dartIndex: dartsToSubmit.length,
                        })
                    }
                }

                for (const { dart, dartIndex } of dartsToSubmit) {
                    const dartData = {
                        profile_id: profileId,
                        dart_type: dart.dart_type,
                        dart_number: dart.dart_number,
                        dart_index: dartIndex,
                    }

                    if (this.matchStore.editingTurn) {
                        await this.matchStore.updateDart(dartData)
                    } else {
                        await this.matchStore.submitDart(dartData)
                    }
                }

                // double_attempts is turn-level metadata. The darts are saved;
                // annotate the just-completed turn in a separate metadata call.
                // Recording the stat must never break the turn, so swallow errors.
                if (doubleAttempts !== null) {
                    try {
                        await this.matchStore.updateTurnData({
                            profile_id: profileId,
                            double_attempts: doubleAttempts,
                        })
                    } catch (error) {
                        console.error('Failed to record double attempts:', error)
                    }
                }

                this.resetDarts()
                this.matchStore.clearEditingTurn()
            } catch (error) {
                console.error('Failed to submit turn:', error)
            } finally {
                this.isSubmitting = false
            }
        },
        handleCheckoutAttemptsSelect(doubleAttempts) {
            this.submitDarts(doubleAttempts)
        },
        handleCheckoutAttemptsCancel() {
            this.showCheckoutAttemptsModal = false
            this.isSubmitting = false
        },
    },
}
</script>
